import {Module} from 'vuex';
import CheckoutApi from "@support/checkoutApi";
import * as h from "@app/components/checkout/helper";
import * as requests from "@app/components/checkout/requests";
import currencyFilter from "@app/util/currency";

/**
 * @type {Module<CheckoutState>}
 */
const checkoutModule = {
    namespaced: true,
    state: {
        isCheckoutRefreshRequested: false,
        isPlaceOrderRequested: false,
        isPaymentRequested: false,
        isCheckoutLoaded: false,
        isFormBlocked: false,
        isRequestProcessing: true,
        isTaxesCalculated: true,
        isLoadingShippingMethods: false,
        isLoadingTaxes: false,
        showPasswordForm: false,
        signingUpUser: false,
        itemsLoading: [],
        obj: {
            products: {},
            bonus_total: '',
            coupon_free_shipping: null,
            coupon_message: '',
            coupons: [],
            discount_total: '',
            formatted_discount_total: 0,
            formatted_shipping_discount_total: '',
            formatted_tax_total: '',
            is_processable: false,
            is_shipped_for_points: false,
            paid_by_gift_certificate: false,
            points: 0,
            promotion_conditions_messages: [],
            promotion_description: null,
            promotion_discount: '',
            promotion_reward_message: null,
            promotion_title: null,
            shipping: '',
            shipping_tax: '',
            shipping_discount: '',
            subtotal: '',
            tax_total: '',
            total: ''
        },
        config: {
            title: null,
            payment_methods: [],
        },
        isConfigLoaded: false,
        selectedDesktopPaymentMethod: null,
        groupMessages: [],
        checkoutErrors: [],
        validator: null,
    },
    mutations: {
        setCheckoutLoaded: (s, p) => s.isCheckoutLoaded = p,
        setFormBlocked: (s, p) => s.isFormBlocked = p,
        setRequestProcessing: (s, p) => s.isRequestProcessing = p,
        setTaxesCalculated: (s, p) => s.isTaxesCalculated = p,
        setLoadingShippingMethods: (s, p) => s.isLoadingShippingMethods = p,
        setLoadingTaxes: (s, p) => s.isLoadingTaxes = p,
        setShowPasswordForm: (s, p) => s.showPasswordForm = p,
        setSigningUpUser: (s, p) => s.signingUpUser = p,
        setObj: (s, p) => s.obj = p,
        setItemRowLoading: (s, {id, status}) => status
          ? s.itemsLoading.push(id)
          : s.itemsLoading = s.itemsLoading.filter(e => e !== id),
        setIsCheckoutRefreshRequested: (s, p) => s.isCheckoutRefreshRequested = p,
        setIsPaymentRequested: (s, p) => s.isPaymentRequested = p,
        setConfig: (s, p) => s.config = p,
        setIsConfigLoaded: (s, p) => s.isConfigLoaded = p,
        setSelectedDesktopPaymentMethod: (s, p) => s.selectedDesktopPaymentMethod = p,
        setIsPlaceOrderRequested: (s, p) => s.isPlaceOrderRequested = p,
        setGroupMessages: (s, p) => s.groupMessages = p,
        setCheckoutErrors: (s, p) => s.checkoutErrors = p,
        setValidator: (s, p) => s.validator = p,
    },
    getters: {
        getAvailablePaymentMethods(state) {
            return state.config.payment_methods.filter(payment_method => payment_method.is_available === true);
        },
        getAvailableDesktopPaymentMethods(state) {
            return state.config.payment_methods.filter(payment_method => payment_method.is_available === true && payment_method.type === 'desktop');
        },
        getAvailableExpressPaymentMethods(state) {
            return state.config.payment_methods.filter(payment_method => payment_method.is_available === true && payment_method.type === 'express');
        },
        getAvailableOneClickPaymentMethods(state) {
            return state.config.payment_methods.filter(payment_method => payment_method.is_available === true && payment_method.type === 'one_click');
        },
        getAvailableMobilePaymentMethods(state) {
            return state.config.payment_methods.filter(payment_method => payment_method.is_available === true && payment_method.type === 'mobile');
        },
        getGiftObject(state) {
            return state.obj.coupons.find(coupon => coupon.type === 'gift') || {};
        },
        items(state) {
            const products = state.obj.products;

            if (products && typeof products === 'object') {
                return Object.keys(products).map(id => products[id]);
            }

            return [];
        },
        checkout(state) {
            return state.obj;
        },
        isProcessable(state, getters) {
            return getters.checkout.is_processable;
        },
        orderUnprocessable(state, getters, rootState) {
          const containsShippableItems = getters.items.filter(function(item) {
            const shippingType = item.options.shipping_type ? item.options.shipping_type : 'ship';

            return 'ship' === shippingType;
          }).length > 0;

          return getters.items.filter(item => item.out_of_stock).length > 0 ||
                getters.items.length === 0 ||
                state.isFormBlocked ||
                (containsShippableItems && rootState.shippingMethods.shipping_methods.length === 0) ||
                rootState.contact.isShowPasswordForm ||
                !state.isTaxesCalculated ||
                !getters.isProcessable;
        },
        discountTotal(state, getters) {
            return currencyFilter(getters.checkout.discount_total) || 0
        },
        total(state, getters) {
            return currencyFilter(getters.checkout.total);
        },
        subTotal(state, getters) {
            return currencyFilter(getters.checkout.subtotal);
        },
        shippingTotal(state, getters, rootState) {
            return currencyFilter(rootState.shipping.price_total) || 0
        },
        taxTotal(state, getters) {
            const itemsTax =  parseFloat(currencyFilter(getters.checkout.tax_total));
            const shippingTax = parseFloat(currencyFilter(getters.checkout.shipping_tax));

            return (itemsTax + shippingTax).toFixed(2);
        },
        isRequestProcessing(state) {
            return state.isRequestProcessing
        },
        isLoadingShippingMethods(state) {
            return state.isLoadingShippingMethods;
        },
        isHasPromotions(state, getters) {
            return getters.checkout.promotion_conditions_messages.length !== 0 || getters.checkout.promotion_reward_message
        },
        isHidePlaceOrderButton(state, getters) {
            return !getters.isPaidByGiftCertificate && !state.selectedDesktopPaymentMethod || state.selectedDesktopPaymentMethod.is_hide_place_order_button;
        },
        isPaidByGiftCertificate(state, getters) {
            console.debug('getters', getters.checkout)
            return getters.checkout.paid_by_gift_certificate;
        },
        selectedDesktopPaymentMethod(state) {
            return state.selectedDesktopPaymentMethod;
        },
        isShowExpressCheckout(state, getters) {
            return getters.getAvailableExpressPaymentMethods.length > 0;
        }
    },
    actions: {
        async fetchCheckoutConfig(c) {
            c.commit('setIsConfigLoaded', false);
            await axios.get('/api/v1/checkout/config')
                .then((response) => {
                    c.commit('setConfig', response.data);
                    const paymentMethod = _.head(c.getters.getAvailableDesktopPaymentMethods);

                    if (paymentMethod) {
                        c.commit('setSelectedDesktopPaymentMethod', paymentMethod);
                    }

                    c.commit('setIsConfigLoaded', true);
                })
                .catch((error) => {
                    console.error(error);
                    c.commit('setConfig', {})
                    c.commit('setIsConfigLoaded', false);
                });
        },
        makePayment(c) {
            c.commit('setIsPaymentRequested', true);
        },
        placeOrder(c) {
            c.commit('setIsPlaceOrderRequested', true);
        },
        updatePaymentMethod({commit, state}, payload) {
            const {oldValue, newValue} = payload;
            const paymentMethods = state.config.payment_methods;
            const index = paymentMethods.indexOf(oldValue);

            if (-1 !== index) {
                // @see https://stackoverflow.com/questions/40860592/vuex-getter-not-updating
                state.config.payment_methods.splice(index, 1, newValue);
            }
        },
        fetchGroupMessages({commit, state}) {
            CheckoutApi.getGroupMessages()
                .then((response) => {
                    commit('setGroupMessages', response.data);
                })
                .catch((error) => {
                    console.log(error);
                })
        },
        fetchCheckout({commit, dispatch, getters}, callback) {
            const setRequestStatus = status => commit('setRequestProcessing', status);
            commit('setCheckoutLoaded', false);

            return CheckoutApi.getCart().then((response) => {
                dispatch('updateCheckout', response.data, {root: true});
                dispatch('fetchGroupMessages');
            }).catch((error) => {
                if (error.cancel) { return error }
                console.error(error);
            }).finally(() => {
                console.debug("Checkout fetched");
                commit('setCheckoutLoaded', true);
                setRequestStatus(false);
                dispatch('fetchTaxes')
                h.updateCartStack(getters.items);

                if (typeof callback === 'function') {
                    callback();
                    commit('setIsCheckoutRefreshRequested', false)
                }

                document.dispatchEvent(new CustomEvent('startedCheckout'));
            })
        },
        fetchTaxes(context) {
            const shipping = context.rootState.shipping;
            const requiredFields = [
                'city',
                'country',
                'state',
                'zip_code',
            ];
            const canCalculateTaxes = (reqFields, shipObject) => reqFields
                .map(field => shipObject[field])
                .filter(e => e)
                .length === reqFields.length;
            const setTaxesCalculatedStatus = (status) => this.isTaxesCalculated = status;

            setTaxesCalculatedStatus(false);

            if (!canCalculateTaxes(requiredFields, shipping)) {
                return false;
            }

            requests.taxes(context);
        },
        addCheckoutErrors({commit, state}, payload) {
            commit('setCheckoutErrors', {...state.checkoutErrors, ...payload})
        },
        clearCheckoutErrors({commit}) {
            commit('setCheckoutErrors', []);
        },
        validateCheckout(context) {
            return context.state.validator.validateAll().then(valid => {
                if (!valid) {
                    return Promise.reject();
                } else {
                    return Promise.resolve();
                }
            });
        },
    }
};

export default checkoutModule;
